import React, { useState, useEffect, useRef } from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Rating,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  PictureAsPdf as PdfIcon,
  Email as EmailIcon,
  ExpandMore as ExpandMoreIcon,
  Link as LinkIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { formatDistanceToNow, parseISO } from 'date-fns';
import api from '../config/api';
import TripItinerary from '../components/TripItinerary';
import TripMembers from '../components/TripMembers';
import TripDocuments from '../components/TripDocuments';
import TripExpenses from '../components/TripExpenses';
import PartnerTripAddOns from '../components/PartnerTripAddOns';
import PartnerTripAIDrawer from '../components/PartnerTripAIDrawer';
import OfflineItineraryBar from '../components/OfflineItineraryBar';
import EmailForwardingCard from '../components/EmailForwardingCard';
import { getTripPrimaryLocation, canEditTripItinerary, showPaidAiBadge } from '../utils/tripUtils';
import { canArchivePartnerTrip } from '../utils/partnerListUtils';
import { PaidFeatureLabel } from '../components/DocumentProBadge';
import { DOCUMENT, documentSubTabsSx, documentTabIndicatorSx } from '../constants/documentTheme';
import ChangeHistoryPanel from '../components/ChangeHistoryPanel';
import {
  PartnerLoading,
  PartnerNote,
  PartnerPageShell,
  PartnerTripHero,
  partnerOxbloodBtnSx,
  partnerOutlineBtnSx,
  partnerLinkSx,
} from '../components/PartnerListChrome';
import PartnerTripDetailsEditor from '../components/PartnerTripDetailsEditor';
import PartnerCapacityDialog from '../components/PartnerCapacityDialog';
import PartnerQuickAdd from '../components/PartnerQuickAdd';
import PartnerRecommendDialog from '../components/PartnerRecommendDialog';
import { partnerCapacityFromError } from '../utils/partnerPlan';
import { partnerTripsReturnPath } from '../utils/partnerWorkspace';
import { PARTNER_AI_CONTEXT_CHIPS, partnerChipAction } from '../utils/partnerRecommendation';
import usePartnerEntitlements from '../hooks/usePartnerEntitlements';
import useOfflinePartnerTrip from '../hooks/useOfflinePartnerTrip';
import { getPartnerOfflineSeatId } from '../utils/partnerPush';
import { canUsePartnerAiPlanning } from '../utils/partnerTierGates';
import PartnerTripFinancePanel, {
  financeFormToPayload,
  financeToForm,
} from '../components/PartnerTripFinancePanel';
import PartnerTripOperationsPanel from '../components/PartnerTripOperationsPanel';
import ShareReadOnlyLinkDialog from '../components/ShareReadOnlyLinkDialog';
import PdfExportProgressDialog from '../components/PdfExportProgressDialog';
import useTripPdfExport from '../hooks/useTripPdfExport';

const PARTNER_AUTH = { usePartnerAuth: true };

const partnerDetailPanelAccordionSx = {
  bgcolor: DOCUMENT.surface,
  border: `1px solid ${DOCUMENT.ruleHard}`,
  borderRadius: 0,
  boxShadow: 'none',
  '&:before': { display: 'none' },
  '&.Mui-expanded': { margin: 0 },
};

const PartnerTripDetails = () => {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pipelineStage, setPipelineStage] = useState('planning');
  const [pipelineSaving, setPipelineSaving] = useState(false);
  const [engagement, setEngagement] = useState(null);
  const [engagementLoading, setEngagementLoading] = useState(false);
  const [nudgeLoading, setNudgeLoading] = useState(false);
  const [financeForm, setFinanceForm] = useState(financeToForm(null));
  const [financePayments, setFinancePayments] = useState([]);
  const [paidFromPayments, setPaidFromPayments] = useState(false);
  const [financeSaving, setFinanceSaving] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [editDetailsOpen, setEditDetailsOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiSeedPrompt, setAiSeedPrompt] = useState('');
  const [addonLaunch, setAddonLaunch] = useState(null);
  const [recommendOpen, setRecommendOpen] = useState(false);
  const [recommendSeed, setRecommendSeed] = useState(null);
  const [capacityOpen, setCapacityOpen] = useState(false);
  const [capacityError, setCapacityError] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [assigneeSaving, setAssigneeSaving] = useState(false);
  const [handoffError, setHandoffError] = useState('');
  const {
    downloadTripPdf,
    pdfProgressOpen,
    pdfProgress,
    pdfProgressPhase,
  } = useTripPdfExport();
  const pdfDownloading = pdfProgressOpen;
  const [emailItineraryOpen, setEmailItineraryOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [readOnlyShareOpen, setReadOnlyShareOpen] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [isOnline, setIsOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine
  );
  const tripMembersRef = useRef();
  const itineraryRef = useRef();
  const aiOpenedFromUrlRef = useRef(false);
  const { entitlements } = usePartnerEntitlements();
  const partnerSeatId = getPartnerOfflineSeatId();
  const offlineTrip = useOfflinePartnerTrip(tripId, partnerSeatId);

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    fetchTripDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

  const fetchEngagement = async () => {
    try {
      setEngagementLoading(true);
      const response = await api.get(`/partners/trips/${tripId}/engagement`, { usePartnerAuth: true });
      setEngagement(response.data?.engagement || null);
    } catch (err) {
      console.warn('Failed to load trip engagement:', err?.message);
      setEngagement(null);
    } finally {
      setEngagementLoading(false);
    }
  };

  useEffect(() => {
    if (!trip?.managed_by_partner && !trip?.partner_id) return;
    fetchEngagement();
    fetchFinance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip?.id, trip?.managed_by_partner, trip?.partner_id]);

  const applyFinance = (next) => {
    setFinanceForm(financeToForm(next));
    setFinancePayments(next?.payments || []);
    setPaidFromPayments(Boolean(next?.paidFromPayments));
  };

  const fetchFinance = async () => {
    try {
      const response = await api.get(`/partners/trips/${tripId}/finance`, { usePartnerAuth: true });
      applyFinance(response.data?.finance || null);
    } catch (err) {
      console.warn('Failed to load trip finance:', err?.message);
    }
  };

  const saveFinance = async () => {
    try {
      setFinanceSaving(true);
      const response = await api.patch(
        `/partners/trips/${tripId}/finance`,
        financeFormToPayload({ ...financeForm, paidFromPayments }, 'full'),
        { usePartnerAuth: true }
      );
      applyFinance(response.data?.finance || null);
    } catch (err) {
      setError(err.response?.data?.error || t('partnerFinance.saveFailed'));
    } finally {
      setFinanceSaving(false);
    }
  };

  const addFinancePayment = async (payment) => {
    try {
      setPaymentSaving(true);
      const response = await api.post(
        `/partners/trips/${tripId}/finance/payments`,
        payment,
        { usePartnerAuth: true }
      );
      applyFinance(response.data?.finance || null);
    } catch (err) {
      setError(err.response?.data?.error || t('partnerFinance.paymentFailed'));
    } finally {
      setPaymentSaving(false);
    }
  };

  const deleteFinancePayment = async (paymentId) => {
    try {
      setPaymentSaving(true);
      const response = await api.delete(
        `/partners/trips/${tripId}/finance/payments/${paymentId}`,
        { usePartnerAuth: true }
      );
      applyFinance(response.data?.finance || null);
    } catch (err) {
      setError(err.response?.data?.error || t('partnerFinance.paymentFailed'));
    } finally {
      setPaymentSaving(false);
    }
  };

  useEffect(() => {
    if (!trip || loading || aiOpenedFromUrlRef.current) return;
    const seed = searchParams.get('aiSeed');
    const openAi = searchParams.get('openAi') === '1';
    if ((openAi || seed) && canEditTripItinerary(trip)) {
      aiOpenedFromUrlRef.current = true;
      setAiSeedPrompt(seed ? decodeURIComponent(seed) : '');
      setAiOpen(true);
    }
  }, [trip, loading, searchParams]);

  useEffect(() => {
    if (loading || !trip) return;
    const tab = searchParams.get('tab');
    if (tab === 'operations') {
      setActiveTab(1);
      return;
    }
    if (tab === 'documents') {
      setActiveTab(3);
      return;
    }
    if (tab === 'itinerary') {
      setActiveTab(0);
    }
  }, [loading, trip, searchParams]);

  useEffect(() => {
    if (loading || !trip || activeTab !== 0) return;
    const focusItem = searchParams.get('focusItem');
    if (!focusItem) return;
    const itemId = Number(focusItem);
    if (!Number.isInteger(itemId)) return;
    const timer = window.setTimeout(() => {
      itineraryRef.current?.focusDrafts?.([{ id: itemId }]);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [loading, trip, activeTab, searchParams]);

  useEffect(() => {
    const loadTeam = async () => {
      try {
        const response = await api.get('/partners/team-members', { usePartnerAuth: true });
        setTeamMembers(Array.isArray(response.data) ? response.data : (response.data?.users || []));
      } catch (err) {
        console.warn('Failed to load partner team for handoff:', err?.message);
        setTeamMembers([]);
      }
    };
    loadTeam();
  }, []);

  const fetchTripDetails = async () => {
    try {
      setLoading(true);
      const partnerToken = localStorage.getItem('partnerToken');

      if (!partnerToken) {
        navigate('/partner/login');
        return;
      }

      const response = await api.get(`/trips/${tripId}`, { usePartnerAuth: true });

      setTrip(response.data);
      if (response.data?.managed_by_partner || response.data?.partner_id) {
        setPipelineStage(response.data.pipeline_stage || 'planning');
      }
    } catch (err) {
      console.error('Error fetching trip:', err);
      if (err.response?.status === 401) {
        navigate('/partner/login');
      } else if (err.response?.status === 404) {
        setError(t('partnerManage.tripNotFound'));
      } else {
        setError(t('partnerManage.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveTrip = async (archived = true) => {
    try {
      setArchiveLoading(true);
      await api.patch(`/partners/trips/${tripId}/archive`, { archived }, PARTNER_AUTH);
      setArchiveDialogOpen(false);
      enqueueSnackbar(
        archived ? t('partnerLists.archiveTrip') : t('partnerLists.restoreTrip'),
        { variant: 'success' }
      );
      if (archived) {
        navigate(partnerTripsReturnPath());
        return;
      }
      setTrip((prev) => (prev ? { ...prev, partner_archived: false } : prev));
    } catch (err) {
      enqueueSnackbar(
        err.response?.data?.error || (
          archived ? t('partnerLists.archiveTripFailed') : t('partnerLists.restoreTripFailed')
        ),
        { variant: 'error' }
      );
      setArchiveDialogOpen(false);
    } finally {
      setArchiveLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    try {
      setInviteError('');
      setInviteSubmitting(true);

      if (tripMembersRef.current?.addOptimisticInvitation) {
        tripMembersRef.current.addOptimisticInvitation(inviteEmail);
      }

      await api.post(`/trips/${tripId}/invite`, { email: inviteEmail }, { usePartnerAuth: true });
      setInviteEmail('');
      setShowInviteDialog(false);
      fetchTripDetails();
    } catch (err) {
      console.error('Invitation error:', err);
      const capacity = partnerCapacityFromError(err);
      if (capacity) {
        setShowInviteDialog(false);
        setCapacityError(capacity);
        setCapacityOpen(true);
      }
      setInviteError(err.response?.data?.error || t('partnerLists.sendFailed'));
      if (tripMembersRef.current?.removeOptimisticInvitation) {
        tripMembersRef.current.removeOptimisticInvitation(inviteEmail);
      }
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleAssigneeChange = async (event) => {
    const value = event.target.value;
    const assignedId = value === '' ? null : Number(value);
    try {
      setHandoffError('');
      setAssigneeSaving(true);
      const response = await api.patch(
        `/partners/trips/${tripId}/assignee`,
        { assigned_partner_user_id: assignedId },
        { usePartnerAuth: true }
      );
      setTrip((prev) => (prev ? {
        ...prev,
        assigned_partner_user_id: response.data.assigned_partner_user_id,
        assigned_partner_user_name: response.data.assigned_partner_user_name,
        assigned_partner_user_email: response.data.assigned_partner_user_email,
      } : prev));
    } catch (err) {
      console.error('Failed to assign trip:', err);
      setHandoffError(err.response?.data?.error || t('partnerManage.handoffFailed'));
    } finally {
      setAssigneeSaving(false);
    }
  };

  const handlePipelineStageChange = async (event) => {
    const stage = event.target.value;
    try {
      setPipelineSaving(true);
      await api.patch(`/partners/trips/${tripId}/pipeline-stage`, { stage }, { usePartnerAuth: true });
      setPipelineStage(stage);
      setTrip((prev) => (prev ? { ...prev, pipeline_stage: stage } : prev));
    } catch (err) {
      setError(err.response?.data?.error || t('partnerPipeline.stageUpdateFailed'));
    } finally {
      setPipelineSaving(false);
    }
  };

  const handleGenerateNudge = async () => {
    try {
      setNudgeLoading(true);
      const response = await api.post(`/partners/trips/${tripId}/engagement/nudge`, {}, { usePartnerAuth: true });
      setEngagement(response.data?.engagement || null);
    } catch (err) {
      setError(err.response?.data?.error || t('partnerEngagement.nudgeFailed'));
    } finally {
      setNudgeLoading(false);
    }
  };

  const formatEngagementDate = (value) => {
    if (!value) return t('partnerEngagement.never');
    try {
      return formatDistanceToNow(parseISO(String(value).replace(' ', 'T')), { addSuffix: true });
    } catch {
      return t('partnerEngagement.never');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('partnerLists.dash');
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleDownloadPdf = async () => {
    if (!trip?.id || pdfDownloading) return;
    try {
      await downloadTripPdf({
        tripId: trip.id,
        tripTitle: trip.title,
        usePartnerAuth: true,
      });
      enqueueSnackbar(t('partnerManage.pdfDownloaded'), { variant: 'success' });
    } catch (err) {
      let message = t('partnerManage.pdfDownloadFailed');
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          if (parsed?.error) message = parsed.error;
        } catch {
          // keep default
        }
      } else if (data?.error || err.message) {
        message = data?.error || err.message;
      }
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const openEmailItinerary = () => {
    setEmailError('');
    setEmailMessage('');
    setEmailTo(trip?.creator_email || '');
    setEmailItineraryOpen(true);
  };

  const handleEmailItinerary = async (event) => {
    event.preventDefault();
    if (!trip?.id || emailSending) return;
    try {
      setEmailSending(true);
      setEmailError('');
      await api.post(
        `/partners/trips/${trip.id}/email-itinerary`,
        {
          to: emailTo.trim() || undefined,
          message: emailMessage.trim() || undefined,
        },
        { usePartnerAuth: true }
      );
      setEmailItineraryOpen(false);
      enqueueSnackbar(t('partnerManage.emailItinerarySent'), { variant: 'success' });
    } catch (err) {
      setEmailError(err.response?.data?.error || t('partnerManage.emailItineraryFailed'));
    } finally {
      setEmailSending(false);
    }
  };

  if (loading) {
    return (
      <PartnerPageShell>
        <PartnerLoading />
      </PartnerPageShell>
    );
  }

  if (error || !trip) {
    return (
      <PartnerPageShell error={error || t('partnerManage.tripNotFound')}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(partnerTripsReturnPath())} sx={partnerLinkSx}>
          {t('partnerManage.backToTrips')}
        </Button>
      </PartnerPageShell>
    );
  }

  const clientName = trip.creator_first_name && trip.creator_last_name
    ? `${trip.creator_first_name} ${trip.creator_last_name}`
    : trip.creator_name || trip.creator_email || 'N/A';

  return (
    <PartnerPageShell>
      <Box sx={{ display: 'flex', gap: 2.5, pt: 4.5, flexWrap: 'wrap' }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(partnerTripsReturnPath())} sx={partnerLinkSx}>
          {t('partnerManage.backToClientTrips')}
        </Button>
        <Button onClick={() => navigate('/partner/dashboard')} sx={partnerLinkSx}>
          {t('partnerManage.dashboard')}
        </Button>
      </Box>

      <PartnerTripHero
        title={trip.title}
        description={trip.description}
        badge={t('partnerManage.partnerManaged')}
        coverSrc={trip.image_url && String(trip.image_url).trim() ? trip.image_url : null}
        dates={`${formatDate(trip.start_date)} – ${formatDate(trip.end_date)}`}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, md: 4 }, mt: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationIcon sx={{ fontSize: 16, color: DOCUMENT.accent }} />
            <Typography sx={{ fontSize: 14, color: DOCUMENT.ivory }}>
              {getTripPrimaryLocation(trip) || t('partnerLists.noDestination')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon sx={{ fontSize: 16, color: DOCUMENT.accent }} />
            <Box>
              <Typography sx={{ fontSize: 14, color: DOCUMENT.ivory }}>
                {t('partnerLists.ownerLabel')} {clientName}
              </Typography>
              {trip.creator_email && (trip.creator_first_name || trip.creator_name) && (
                <Typography sx={{ fontSize: 12, color: 'rgba(246,239,228,0.78)' }}>
                  {trip.creator_email}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </PartnerTripHero>

      {(trip.rating_summary?.count > 0 || (trip.ratings || []).length > 0) && (
        <Box
          sx={{
            mt: 2.5,
            p: 2.5,
            bgcolor: DOCUMENT.surface,
            border: `1px solid ${DOCUMENT.ruleHard}`,
          }}
          data-testid="partner-trip-ratings"
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1 }}>
            {t('tripRating.partnerHeading')}
          </Typography>
          {trip.rating_summary?.count > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Rating value={Number(trip.rating_summary.average) || 0} precision={0.1} readOnly size="small" />
              <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2 }}>
                {t('tripRating.partnerAverage', {
                  average: trip.rating_summary.average,
                  count: trip.rating_summary.count,
                })}
              </Typography>
            </Box>
          )}
          {(trip.ratings || []).map((item, index) => (
            <Box key={`${item.travelerName || 'traveler'}-${index}`} sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                {item.travelerName || t('tripRating.advisorLabel')}
              </Typography>
              <Rating value={item.stars || 0} readOnly size="small" />
              {item.comment ? (
                <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2 }}>{item.comment}</Typography>
              ) : null}
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, mt: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button startIcon={<EditIcon />} onClick={() => setEditDetailsOpen(true)} sx={partnerOutlineBtnSx}>
          {t('partnerManage.editDetails')}
        </Button>
        {(trip.partner_archived || canArchivePartnerTrip(trip)) && (
          <Button
            startIcon={trip.partner_archived ? <UnarchiveIcon /> : <ArchiveIcon />}
            onClick={() => setArchiveDialogOpen(true)}
            sx={partnerOutlineBtnSx}
            data-testid="partner-trip-archive"
          >
            {trip.partner_archived ? t('partnerLists.restoreTrip') : t('partnerLists.archiveTrip')}
          </Button>
        )}
        <Button
          startIcon={<PdfIcon />}
          onClick={handleDownloadPdf}
          disabled={pdfDownloading}
          sx={partnerOutlineBtnSx}
          data-testid="partner-trip-download-pdf"
        >
          {pdfDownloading ? t('partnerManage.pdfDownloading') : t('partnerManage.downloadPdf')}
        </Button>
        <Button
          startIcon={<EmailIcon />}
          onClick={openEmailItinerary}
          sx={partnerOutlineBtnSx}
          data-testid="partner-trip-email-itinerary"
        >
          {t('partnerManage.emailItinerary')}
        </Button>
        <Button
          startIcon={<LinkIcon />}
          onClick={() => setReadOnlyShareOpen(true)}
          sx={partnerOutlineBtnSx}
          data-testid="partner-trip-share-link"
        >
          {t('tripShare.openFromShare')}
        </Button>
        {canEditTripItinerary(trip) && (
        <Button
          onClick={() => {
            setAiSeedPrompt('');
            setAiOpen(true);
          }}
          sx={partnerOutlineBtnSx}
          data-testid="partner-trip-ai"
        >
          <PaidFeatureLabel show={showPaidAiBadge(trip, { entitlements })}>
            {t('partnerManage.suggestWithAi')}
          </PaidFeatureLabel>
        </Button>
        )}
        {trip.inquiry_id && (
          <Button onClick={() => navigate(`/partner/inquiries/${trip.inquiry_id}`)} sx={partnerOutlineBtnSx}>
            {t('partnerManage.messages')}
          </Button>
        )}
        <FormControl size="small" sx={{ minWidth: 200 }} data-testid="partner-trip-pipeline-stage">
          <InputLabel id="partner-trip-pipeline-label">{t('partnerLists.colStatus')}</InputLabel>
          <Select
            labelId="partner-trip-pipeline-label"
            label={t('partnerLists.colStatus')}
            value={pipelineStage}
            onChange={handlePipelineStageChange}
            disabled={pipelineSaving}
            sx={{ borderRadius: 0, bgcolor: DOCUMENT.surface }}
          >
            {['inquiry', 'planning', 'proposal_sent', 'approved', 'booked', 'traveling', 'completed'].map((stage) => (
              <MenuItem key={stage} value={stage}>
                {t(`partnerPipeline.stages.${stage}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mt: 2.5 }}>
        <Accordion
          defaultExpanded={false}
          disableGutters
          elevation={0}
          data-testid="partner-trip-finance-accordion"
          sx={partnerDetailPanelAccordionSx}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: DOCUMENT.oxblood }} />}
            sx={{ px: 2.5, minHeight: 56, '& .MuiAccordionSummary-content': { my: 1.25 } }}
          >
            <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 20 }}>
              {t('partnerFinance.title')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
            <PartnerTripFinancePanel
              form={financeForm}
              onChange={(patch) => setFinanceForm((p) => ({ ...p, ...patch }))}
              onSave={saveFinance}
              saving={financeSaving}
              showTitle={false}
              showStatusChip
              embedded
              payments={financePayments}
              paidFromPayments={paidFromPayments}
              onAddPayment={addFinancePayment}
              onDeletePayment={deleteFinancePayment}
              paymentSaving={paymentSaving}
            />
          </AccordionDetails>
        </Accordion>
      </Box>

      <Accordion
        defaultExpanded={false}
        disableGutters
        elevation={0}
        data-testid="partner-trip-engagement"
        sx={{ ...partnerDetailPanelAccordionSx, mt: 2.5 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: DOCUMENT.oxblood }} />}
          sx={{ px: 2.5, minHeight: 56, '& .MuiAccordionSummary-content': { my: 1.25 } }}
        >
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 20 }}>
            {t('partnerEngagement.title')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
        {engagementLoading ? (
          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3 }}>{t('partnerLists.loadingShort')}</Typography>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
              <Box>
                <Typography sx={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
                  {t('partnerEngagement.views')}
                </Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 600 }}>{engagement?.viewCount ?? 0}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
                  {t('partnerEngagement.uniqueDays')}
                </Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 600 }}>{engagement?.uniqueDays ?? 0}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
                  {t('partnerEngagement.lastViewed')}
                </Typography>
                <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2 }}>
                  {formatEngagementDate(engagement?.lastViewedAt)}
                </Typography>
              </Box>
            </Box>
            {engagement?.topItems?.length ? (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1, color: DOCUMENT.ink2 }}>
                  {t('partnerEngagement.topItems')}
                </Typography>
                {engagement.topItems.map((item) => (
                  <Typography key={item.itineraryItemId} sx={{ fontSize: 13, color: DOCUMENT.ink2, mb: 0.5 }}>
                    {item.rank}. {item.title} ({item.views})
                  </Typography>
                ))}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mb: 2 }}>
                {t('partnerEngagement.noViewsYet')}
              </Typography>
            )}
            {engagement?.aiNudge ? (
              <Alert severity="info" sx={{ mb: 2, borderRadius: 0 }}>
                {engagement.aiNudge}
              </Alert>
            ) : null}
            <Button
              onClick={handleGenerateNudge}
              disabled={nudgeLoading || !(engagement?.viewCount > 0)}
              sx={partnerOutlineBtnSx}
            >
              {nudgeLoading ? t('partnerLists.loadingShort') : t('partnerEngagement.generateNudge')}
            </Button>
            {!(engagement?.viewCount > 0) ? (
              <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 1 }}>
                {t('partnerEngagement.nudgeNeedsViews')}
              </Typography>
            ) : null}
          </>
        )}
        </AccordionDetails>
      </Accordion>

      <FormControl
        size="small"
        sx={{ mt: 2, minWidth: 220, maxWidth: 360 }}
        data-testid="partner-trip-handoff"
      >
        <InputLabel id="partner-trip-assignee-label">{t('partnerManage.handoffLabel')}</InputLabel>
        <Select
          labelId="partner-trip-assignee-label"
          label={t('partnerManage.handoffLabel')}
          value={trip.assigned_partner_user_id || ''}
          onChange={handleAssigneeChange}
          disabled={assigneeSaving}
        >
          <MenuItem value="">
            <em>{t('partnerManage.handoffUnassigned')}</em>
          </MenuItem>
          {teamMembers.map((member) => (
            <MenuItem key={member.id} value={member.id}>
              {member.name || member.email}
              {member.role ? ` (${member.role})` : ''}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {handoffError ? (
        <Alert severity="error" sx={{ mt: 1, maxWidth: 420 }} onClose={() => setHandoffError('')}>
          {handoffError}
        </Alert>
      ) : null}

      {canEditTripItinerary(trip) && canUsePartnerAiPlanning(entitlements) ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mt: 2,
          }}
          data-testid="partner-trip-ai-chips"
        >
          {PARTNER_AI_CONTEXT_CHIPS.map((chip) => (
            <Chip
              key={chip.id}
              clickable
              label={t(`partner.opsHome.aiChips.${chip.id}`)}
              data-testid={`partner-trip-chip-${chip.id}`}
              onClick={() => {
                const action = partnerChipAction(chip.id, {
                  trip,
                  location: getTripPrimaryLocation(trip) || '',
                });
                if (action?.type === 'message') {
                  navigate(action.path);
                  return;
                }
                if (action?.type === 'inventory') {
                  setActiveTab(4);
                  setAddonLaunch({ kind: 'inventory', query: action.query, token: Date.now() });
                  return;
                }
                if (action?.type === 'itinerary') {
                  setActiveTab(0);
                  setTimeout(() => itineraryRef.current?.openAddActivityDialog?.(action.activity), 50);
                }
              }}
              sx={{
                borderRadius: 0,
                bgcolor: DOCUMENT.surface,
                border: `1px solid ${DOCUMENT.ruleHard}`,
                fontFamily: DOCUMENT.sans,
                fontSize: 12,
              }}
            />
          ))}
        </Box>
      ) : null}

      <PartnerNote>{t('partnerManage.partnerViewNote')}</PartnerNote>

      <Box sx={{ bgcolor: DOCUMENT.surface, border: `1px solid ${DOCUMENT.ruleHard}`, mt: 2.5 }}>
        <Box sx={{ px: { xs: 2, md: 3 } }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            TabIndicatorProps={{ sx: documentTabIndicatorSx }}
            sx={documentSubTabsSx}
          >
            <Tab label={t('tripTabs.itinerary')} />
            <Tab label={t('tripTabs.operations')} />
            <Tab label={t('tripTabs.membersCount', { count: trip.member_count || 0 })} />
            <Tab label={t('partnerManage.documents')} />
            <Tab label={t('partnerManage.addons')} />
            <Tab label={t('tripTabs.expenses')} data-testid="partner-trip-expenses-tab" />
            <Tab label={t('changeHistory.historyTab')} data-testid="partner-trip-tab-history" />
          </Tabs>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {activeTab === 0 && (
            <>
              <OfflineItineraryBar
                supported={offlineTrip.supported}
                isOnline={isOnline}
                isSaved={offlineTrip.isSaved}
                savedAt={offlineTrip.savedAt}
                saving={offlineTrip.saving}
                error={offlineTrip.error}
                hasTripPass
                onSave={offlineTrip.save}
                onRemove={offlineTrip.remove}
              />
              <EmailForwardingCard compact variant="partner" context="tripDetail" />
              <TripItinerary
                ref={itineraryRef}
                tripId={tripId}
                isCreator={true}
                isPartner={true}
                trip={trip}
                partnerEntitlements={entitlements}
                onSuggestWithAi={canEditTripItinerary(trip)
                  ? () => {
                    setAiSeedPrompt('');
                    setAiOpen(true);
                  }
                  : undefined}
                onItineraryChange={fetchTripDetails}
              />
            </>
          )}
          {activeTab === 1 && (
            <PartnerTripOperationsPanel
              tripId={tripId}
              trip={trip}
              onOpenItinerary={(itemId) => {
                setActiveTab(0);
                if (itemId === 'add') {
                  window.setTimeout(() => {
                    itineraryRef.current?.openAddActivityDialog?.();
                  }, 150);
                  return;
                }
                if (itemId != null && itemId !== '') {
                  window.setTimeout(() => {
                    itineraryRef.current?.focusDrafts?.([{ id: Number(itemId) }]);
                  }, 150);
                }
              }}
              onDownloadPdf={handleDownloadPdf}
              onEmailItinerary={openEmailItinerary}
              pdfBusy={pdfDownloading}
              onChanged={fetchTripDetails}
            />
          )}          {activeTab === 2 && (
            <TripMembers
              tripId={tripId}
              trip={trip}
              isCreator={true}
              isPartner={true}
              onInviteClick={() => {
                setInviteError('');
                setShowInviteDialog(true);
              }}
              onMemberChange={fetchTripDetails}
              ref={tripMembersRef}
            />
          )}
          {activeTab === 3 && (
            <TripDocuments
              tripId={tripId}
              isCreator={true}
              isPartner={true}
              isActive={activeTab === 3}
              focusDocumentId={searchParams.get('doc')}
              onFocusDocumentHandled={() => {
                if (!searchParams.has('doc')) return;
                const next = new URLSearchParams(searchParams);
                next.delete('doc');
                const qs = next.toString();
                navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
              }}
            />
          )}
          {activeTab === 4 && (
            <PartnerTripAddOns
              tripId={tripId}
              isActive={activeTab === 4}
              launch={addonLaunch}
              onLaunchHandled={() => setAddonLaunch(null)}
            />
          )}
          {activeTab === 5 && (
            <TripExpenses tripId={tripId} trip={trip} isPartner />
          )}
          {activeTab === 6 && (
            <ChangeHistoryPanel
              endpoint={`/trips/${tripId}/history`}
              requestConfig={PARTNER_AUTH}
              subtitle={t('changeHistory.tripSubtitle')}
              testId="partner-trip-history"
            />
          )}
        </Box>
      </Box>

      <PartnerQuickAdd
        onPick={(seed) => {
          setActiveTab(0);
          setTimeout(() => itineraryRef.current?.openAddActivityDialog?.(seed), 50);
        }}
        onRecommend={(seed) => {
          setRecommendSeed(seed);
          setRecommendOpen(true);
        }}
        onOpenDocuments={() => setActiveTab(3)}
        onPhoto={async (url) => {
          setActiveTab(0);
          await itineraryRef.current?.addPhotoItem?.(url);
          await fetchTripDetails();
        }}
      />

      <PartnerRecommendDialog
        open={recommendOpen}
        onClose={() => setRecommendOpen(false)}
        trip={trip}
        seed={recommendSeed}
        onSent={() => {
          setActiveTab(0);
          fetchTripDetails();
        }}
      />

      <PartnerTripAIDrawer
        open={aiOpen}
        onClose={() => {
          setAiOpen(false);
          setAiSeedPrompt('');
        }}
        trip={trip}
        seedPrompt={aiSeedPrompt}
        onAdded={async (created) => {
          setActiveTab(0);
          await fetchTripDetails();
          await itineraryRef.current?.refreshItinerary?.();
          itineraryRef.current?.focusDrafts?.(created);
        }}
      />

      <Dialog
        open={showInviteDialog}
        onClose={() => setShowInviteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleInvite}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontFamily: DOCUMENT.display }}>
            <PersonAddIcon />
            {t('partnerManage.inviteTitle')}
          </DialogTitle>
          <DialogContent>
            {inviteError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {inviteError}
              </Alert>
            )}
            <TextField
              fullWidth
              label={t('partnerManage.inviteEmail')}
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder={t('partnerManage.invitePlaceholder')}
              autoFocus
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setShowInviteDialog(false)} sx={partnerLinkSx}>
              {t('partnerLists.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={!inviteEmail.trim() || inviteSubmitting}
              sx={partnerOxbloodBtnSx}
            >
              {inviteSubmitting ? t('partnerManage.sending') : t('partnerManage.sendInvite')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={emailItineraryOpen}
        onClose={() => !emailSending && setEmailItineraryOpen(false)}
        maxWidth="sm"
        fullWidth
        data-testid="partner-email-itinerary-dialog"
      >
        <form onSubmit={handleEmailItinerary}>
          <DialogTitle sx={{ fontFamily: DOCUMENT.display }}>
            {t('partnerManage.emailItineraryTitle')}
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2 }}>
              {t('partnerManage.emailItineraryHint')}
            </Typography>
            {emailError ? <Alert severity="error">{emailError}</Alert> : null}
            <TextField
              required
              fullWidth
              type="email"
              label={t('partnerManage.emailItineraryTo')}
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              inputProps={{ 'data-testid': 'partner-email-itinerary-to' }}
            />
            <TextField
              fullWidth
              multiline
              minRows={3}
              label={t('partnerManage.emailItineraryMessage')}
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              placeholder={t('partnerManage.emailItineraryMessagePlaceholder')}
              inputProps={{ 'data-testid': 'partner-email-itinerary-message' }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setEmailItineraryOpen(false)}
              disabled={emailSending}
              sx={partnerLinkSx}
            >
              {t('partnerLists.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={emailSending || !emailTo.trim()}
              sx={partnerOxbloodBtnSx}
              data-testid="partner-email-itinerary-submit"
            >
              {emailSending ? t('partnerManage.sending') : t('partnerManage.emailItinerarySend')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={archiveDialogOpen} onClose={() => !archiveLoading && setArchiveDialogOpen(false)}>
        <DialogTitle>
          {trip?.partner_archived
            ? t('partnerLists.restoreTripTitle')
            : t('partnerLists.archiveTripTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2 }}>
            {trip?.partner_archived
              ? t('partnerLists.restoreTripBody', { title: trip?.title })
              : t('partnerLists.archiveTripBody', { title: trip?.title })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveDialogOpen(false)} disabled={archiveLoading}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            onClick={() => handleArchiveTrip(!trip?.partner_archived)}
            disabled={archiveLoading}
            sx={partnerOxbloodBtnSx}
            data-testid="partner-trip-archive-confirm"
          >
            {archiveLoading
              ? t('partnerLists.loadingShort')
              : (trip?.partner_archived ? t('partnerLists.restoreTrip') : t('partnerLists.archiveTrip'))}
          </Button>
        </DialogActions>
      </Dialog>

      <ShareReadOnlyLinkDialog
        open={readOnlyShareOpen}
        onClose={() => setReadOnlyShareOpen(false)}
        tripId={tripId}
        usePartnerAuth
      />

      <PartnerTripDetailsEditor
        open={editDetailsOpen}
        onClose={() => setEditDetailsOpen(false)}
        trip={trip}
        mode="client"
        onSaved={fetchTripDetails}
      />

      {capacityOpen && (
        <PartnerCapacityDialog
          open={capacityOpen}
          limitError={capacityError}
          onClose={() => setCapacityOpen(false)}
          onSuccess={() => {
            setCapacityOpen(false);
            fetchTripDetails();
          }}
        />
      )}
      <PdfExportProgressDialog
        open={pdfProgressOpen}
        progress={pdfProgress}
        phase={pdfProgressPhase}
      />
    </PartnerPageShell>
  );
};

export default PartnerTripDetails;
